import { getProductColorOptions } from '@loomstateorg/shared-frontend/components/product-color-swatches/helpers';
import type { ProductColorOption } from '@loomstateorg/shared-frontend/components/product-color-swatches/types';

type ProductLike = {
  variants?: Parameters<typeof getProductColorOptions>[0];
};

const normalizeColorValue = (value: string): string => value.trim().toLowerCase();

/** Collects unique colors in the same order as product-card swatches (first variant appearance). */
export const buildColorOptionsFromProducts = (products: ProductLike[]): ProductColorOption[] => {
  const options = new Map<string, ProductColorOption>();

  products.forEach((product) => {
    getProductColorOptions(product.variants).forEach((option) => {
      if (!options.has(option.key)) {
        options.set(option.key, option);
      }
    });
  });

  return [...options.values()];
};

export const resolveColorOptionForFacetTerm = (
  colorOptions: ProductColorOption[],
  termKey: string,
  termLabel?: string,
): ProductColorOption | undefined => {
  const normalizedKey = normalizeColorValue(termKey);
  const normalizedLabel = termLabel ? normalizeColorValue(termLabel) : normalizedKey;

  return colorOptions.find(
    (option) =>
      normalizeColorValue(option.key) === normalizedKey ||
      normalizeColorValue(option.label) === normalizedKey ||
      normalizeColorValue(option.label) === normalizedLabel ||
      normalizeColorValue(option.key) === normalizedLabel,
  );
};

type FacetTermLike = {
  key: string;
  label: string;
  count: number;
  selected?: boolean;
  isRefined?: boolean;
};

/** Reorders facet terms to follow the product-card swatch order, keeping unknown terms at the end. */
export const orderFacetTermsByColorOptions = <T extends FacetTermLike>(
  terms: T[],
  colorOptions: ProductColorOption[],
): T[] => {
  const visibleTerms = terms.filter((term) => term.count > 0 || term.selected || term.isRefined);

  const orderByKey = new Map(colorOptions.map((option, index) => [normalizeColorValue(option.key), index]));
  const orderByLabel = new Map(colorOptions.map((option, index) => [normalizeColorValue(option.label), index]));

  const getSortIndex = (term: T): number => {
    const byKey = orderByKey.get(normalizeColorValue(term.key));
    if (byKey !== undefined) return byKey;

    const byLabel = orderByLabel.get(normalizeColorValue(term.label)) ?? orderByLabel.get(normalizeColorValue(term.key));
    return byLabel ?? Number.MAX_SAFE_INTEGER;
  };

  return [...visibleTerms].sort((a, b) => {
    const indexDiff = getSortIndex(a) - getSortIndex(b);
    return indexDiff !== 0 ? indexDiff : a.label.localeCompare(b.label);
  });
};
